import { AlertTriangle, CreditCard, ExternalLink, Loader2, RefreshCw } from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("it-IT", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function historyStatusClass(row) {
  const status = String(row?.status || "").toLowerCase();
  if (["paid", "completed"].includes(status)) return "bg-emerald-100 text-emerald-800 border-emerald-200";
  if (["failed", "past_due", "unpaid", "uncollectible", "subscription_canceled", "canceled"].includes(status)) {
    return "bg-rose-100 text-rose-800 border-rose-200";
  }
  if (["open", "pending", "initiated", "awaiting_transfer"].includes(status)) {
    return "bg-amber-100 text-amber-800 border-amber-200";
  }
  return "bg-slate-100 text-slate-700 border-slate-200";
}

export function pagamentoListBadges(user) {
  const badges = [];
  if (user?.pending_bank_transfer) {
    badges.push({ key: "bonifico", label: "Bonifico in attesa", className: "bg-amber-100 text-amber-800 border border-amber-300" });
  }
  if (user?.pending_stripe_payment) {
    badges.push({ key: "stripe-pending", label: "Pagamento Stripe in attesa", className: "bg-sky-100 text-sky-800 border border-sky-300" });
  }
  if (user?.stripe_past_due) {
    badges.push({ key: "stripe-due", label: "Stripe: pagamento scaduto", className: "bg-orange-100 text-orange-800 border border-orange-300" });
  }
  if (user?.stripe_canceled) {
    badges.push({ key: "stripe-canceled", label: "Abbonamento cancellato", className: "bg-rose-100 text-rose-800 border border-rose-300" });
  }
  return badges;
}

function HistoryTable({ title, rows }) {
  if (!rows?.length) return null;
  return (
    <div>
      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</p>
      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-left text-[11px] uppercase text-slate-500">
            <tr>
              <th className="px-2 py-1.5">Data</th>
              <th className="px-2 py-1.5">Tipo</th>
              <th className="px-2 py-1.5">Importo</th>
              <th className="px-2 py-1.5">Stato</th>
              <th className="px-2 py-1.5"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((row) => (
              <tr key={`${row.source}-${row.id}`}>
                <td className="whitespace-nowrap px-2 py-1.5">{formatDate(row.completed_at || row.created_at)}</td>
                <td className="px-2 py-1.5">{row.kind_label || row.number || "—"}</td>
                <td className="px-2 py-1.5">{row.amount || "—"}</td>
                <td className="px-2 py-1.5">
                  <Badge className={`${historyStatusClass(row)} text-[10px]`}>{row.status_label || row.status || "—"}</Badge>
                </td>
                <td className="px-2 py-1.5 text-right">
                  {row.invoice_url ? (
                    <a href={row.invoice_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-slate-600 hover:text-slate-900">
                      Fattura <ExternalLink className="ml-0.5 h-3 w-3" />
                    </a>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function AdminPagamentoCard({ pagamento, onRefresh, refreshing = false }) {
  if (!pagamento) return null;
  const live = pagamento.stripe_live;
  const showPaidBadge = pagamento.transaction_status === "COMPLETED" && !pagamento.stripe_canceled && !pagamento.stripe_past_due;
  return (
    <div className={`rounded-2xl border p-4 space-y-3 ${pagamento.payment_issue ? "border-rose-200 bg-rose-50/40" : "border-slate-200/80 bg-white shadow-[0_1px_4px_rgba(15,23,42,0.04)]"}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
            <CreditCard className="h-3.5 w-3.5" /> Pagamento
          </p>
          <p className="font-medium text-slate-900">{pagamento.method_label || "—"}</p>
          <p className="text-xs text-slate-500">{pagamento.org_status_label || pagamento.org_status || "—"}</p>
        </div>
        <div className="flex flex-wrap items-center gap-1">
          {pagamento.pending_stripe ? (
            <Badge className="bg-sky-100 text-sky-800 border-sky-200">Checkout in attesa</Badge>
          ) : null}
          {pagamento.checkout_failed ? (
            <Badge className="bg-rose-100 text-rose-800 border-rose-200">Checkout fallito</Badge>
          ) : null}
          {pagamento.stripe_past_due ? (
            <Badge className="bg-orange-100 text-orange-800 border-orange-200">Pagamento scaduto</Badge>
          ) : null}
          {pagamento.stripe_canceled ? (
            <Badge className="bg-rose-100 text-rose-800 border-rose-200">Abbonamento cancellato</Badge>
          ) : null}
          {showPaidBadge ? (
            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Pagato</Badge>
          ) : null}
        </div>
      </div>
      {pagamento.stripe_canceled ? (
        <p className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-2 py-1.5 text-xs text-rose-900">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          Abbonamento cancellato: il login della struttura è bloccato finché non viene riattivato o sbloccato con accesso forzato.
        </p>
      ) : null}
      {pagamento.stripe_past_due ? (
        <p className="flex items-start gap-2 rounded-md border border-orange-200 bg-orange-50 px-2 py-1.5 text-xs text-orange-900">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          Pagamento ricorrente non riuscito. Controlla lo storico e Stripe: se l&apos;abbonamento viene cancellato, gli utenti passano a inattivi.
        </p>
      ) : null}
      {live?.alert ? (
        <p className="rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-900">{live.alert}</p>
      ) : null}
      <dl className="grid grid-cols-2 gap-2 text-xs">
        <div>
          <dt className="text-slate-500">Transazione</dt>
          <dd className="font-medium">{pagamento.transaction_status_label || "—"}</dd>
        </div>
        <div>
          <dt className="text-slate-500">Avviata</dt>
          <dd className="font-medium">{formatDate(pagamento.created_at)}</dd>
        </div>
        {pagamento.completed_at ? (
          <div>
            <dt className="text-slate-500">Completata</dt>
            <dd className="font-medium">{formatDate(pagamento.completed_at)}</dd>
          </div>
        ) : null}
        {pagamento.billing_note ? (
          <div className="col-span-2">
            <dt className="text-slate-500">Note</dt>
            <dd className="font-medium">{pagamento.billing_note}</dd>
          </div>
        ) : null}
        {pagamento.stripe_subscription_id ? (
          <div className="col-span-2">
            <dt className="text-slate-500">Abbonamento Stripe</dt>
            <dd className="font-mono text-[11px] break-all">{pagamento.stripe_subscription_id}</dd>
          </div>
        ) : null}
        {pagamento.session_id && String(pagamento.session_id).startsWith("cs_") ? (
          <div className="col-span-2">
            <dt className="text-slate-500">Checkout session</dt>
            <dd className="font-mono text-[11px] break-all">{pagamento.session_id}</dd>
          </div>
        ) : null}
      </dl>
      {live?.mismatch ? (
        <p className="rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-900">{live.mismatch}</p>
      ) : null}
      {live && !live.skipped ? (
        live.ok ? (
          <dl className="grid grid-cols-2 gap-2 rounded-md border border-slate-100 bg-slate-50 px-2 py-2 text-xs">
            {live.session_status_label ? (
              <div>
                <dt className="text-slate-500">Sessione Stripe</dt>
                <dd className="font-medium">{live.session_status_label}</dd>
              </div>
            ) : null}
            {live.payment_status_label ? (
              <div>
                <dt className="text-slate-500">Esito pagamento</dt>
                <dd className="font-medium">{live.payment_status_label}</dd>
              </div>
            ) : null}
            {live.amount ? (
              <div>
                <dt className="text-slate-500">Importo</dt>
                <dd className="font-medium">{live.amount}</dd>
              </div>
            ) : null}
            {live.session_expires_at ? (
              <div>
                <dt className="text-slate-500">Sessione scade</dt>
                <dd className="font-medium">{formatDate(live.session_expires_at)}</dd>
              </div>
            ) : null}
            {live.subscription_status_label ? (
              <div>
                <dt className="text-slate-500">Abbonamento</dt>
                <dd className="font-medium">{live.subscription_status_label}</dd>
              </div>
            ) : null}
            {live.current_period_end ? (
              <div>
                <dt className="text-slate-500">Periodo fino al</dt>
                <dd className="font-medium">{formatDate(live.current_period_end)}</dd>
              </div>
            ) : null}
            {live.cancel_at_period_end ? (
              <div className="col-span-2 text-amber-800">Disdetto: resta attivo fino a fine periodo.</div>
            ) : null}
          </dl>
        ) : (
          <p className="rounded-md border border-rose-200 bg-rose-50 px-2 py-1.5 text-xs text-rose-800">
            {live.error || "Impossibile leggere lo stato su Stripe"}
          </p>
        )
      ) : null}
      <HistoryTable title="Storico Affidya" rows={pagamento.history_locale} />
      <HistoryTable title="Fatture Stripe" rows={pagamento.history_stripe} />
      {!pagamento.history_stripe?.length && pagamento.can_refresh_stripe ? (
        <p className="text-[11px] text-slate-500">Usa «Aggiorna da Stripe» per caricare le fatture dell&apos;abbonamento.</p>
      ) : null}
      <div className="flex flex-wrap gap-2">
        {onRefresh && pagamento.can_refresh_stripe ? (
          <Button size="sm" variant="outline" disabled={refreshing} onClick={onRefresh}>
            {refreshing ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-1 h-3.5 w-3.5" />}
            Aggiorna da Stripe
          </Button>
        ) : null}
        {pagamento.dashboard_url ? (
          <Button size="sm" variant="ghost" asChild>
            <a href={pagamento.dashboard_url} target="_blank" rel="noopener noreferrer">
              Apri in Stripe <ExternalLink className="ml-1 h-3 w-3 opacity-60" />
            </a>
          </Button>
        ) : null}
      </div>
    </div>
  );
}
